// NHB API Service for Robot Management
import { NHBRobot, NHBRobotStatusResponse, NHBRobotDetailsResponse, Robot } from '../types';

class NHBApiService {
  private readonly BASE_URL = '/api/robots/proxy'; // Use our backend proxy

  /**
   * Get all robots status
   */
  async getRobotsStatus(): Promise<NHBRobot[]> {
    try {
      const response = await fetch(`${this.BASE_URL}/status/?include_battery=true&include_location=true`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NHBRobotStatusResponse = await response.json();

      if (!data.success) {
        throw new Error('API returned success: false');
      }

      return data.data.robots;
    } catch (error) {
      console.error('Error fetching robots status:', error);
      throw error;
    }
  }

  /**
   * Get specific robot details
   */
  async getRobotDetails(robotId: string): Promise<NHBRobot> {
    try {
      const response = await fetch(`${this.BASE_URL}/${robotId}/details/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NHBRobotDetailsResponse = await response.json();

      if (!data.success) {
        throw new Error('API returned success: false');
      }

      return data.data;
    } catch (error) {
      console.error(`Error fetching robot ${robotId} details:`, error);
      throw error;
    }
  }

  /**
   * Get robots filtered by museum
   */
  async getRobotsByMuseum(museumCode: string): Promise<NHBRobot[]> {
    try {
      // Validate museum code
      if (!museumCode) {
        throw new Error('Museum code is required');
      }

      const allRobots = await this.getRobotsStatus();

      // Filter robots by museum code
      const museumRobots = allRobots.filter(robot => {
        const matches = robot.museum.toLowerCase() === museumCode.toLowerCase();
        console.log(`🔍 Robot ${robot.robot_id} museum "${robot.museum}" matches "${museumCode}":`, matches);
        return matches;
      });

      return museumRobots;
    } catch (error) {
      console.error(`Error fetching robots for museum ${museumCode}:`, error);
      throw error;
    }
  }

  /**
   * Map NHB robot data to our internal Robot interface
   */
  mapToInternalRobot(nhbRobot: NHBRobot, museumName: string): Robot {
    return {
      id: nhbRobot.robot_id, // Use robot_id as id
      robot_id: nhbRobot.robot_id,
      name: nhbRobot.name || `${nhbRobot.robot_id}`,
      museum_code: nhbRobot.museum,
      museum_name: museumName,
      status: nhbRobot.status, // Use actual NHB status
      is_online: nhbRobot.is_online,
      battery_level: nhbRobot.battery_level,
      battery_status: nhbRobot.battery_status,
      location: nhbRobot.location,
      current_tour: nhbRobot.current_tour,
      health_status: nhbRobot.health_status,
      last_seen: nhbRobot.last_seen,
      state: nhbRobot.state,
      timestamp: nhbRobot.timestamp,
    };
  }

  /**
   * Assign a tour to a robot
   */
  async assignTourToRobot(robotId: string, tourId: number): Promise<any> {
    try {
      const response = await fetch(`/api/robots/${robotId}/tours/assign/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ tour_id: tourId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error assigning tour ${tourId} to robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Start a tour on a robot
   */
  async startTourOnRobot(robotId: string, tourId: number): Promise<any> {
    try {
      const response = await fetch(`/api/robots/${robotId}/tours/start/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ tour_id: tourId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error starting tour ${tourId} on robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Get available tours on a robot (installed tours from API gateway)
   */
  async getRobotAvailableTours(robotId: string, museumCode?: string): Promise<any> {
    try {
      let url = `/api/robots/${robotId}/tours/available/`;
      if (museumCode) {
        url += `?museum=${museumCode}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error getting available tours for robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Get activated tours on a robot
   * Robots poll this endpoint to learn which tours should be displayed.
   * Activation is optimistic - tours are marked active immediately after activation.
   */
  async getRobotActivatedTours(robotId: string, museumCode?: string, museumLevel?: string): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (museumCode) params.append('museum_code', museumCode);
      if (museumLevel) params.append('museum_level', museumLevel);
      const queryString = params.toString();
      const url = `/api/robots/${robotId}/tours/activated/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error getting activated tours for robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Get installed tours on a robot
   */
  async getRobotInstalledTours(robotId: string, museumCode?: string, museumLevel?: string): Promise<any> {
    try {
      let url = `/api/robots/${robotId}/tours/installed/`;
      const params = new URLSearchParams();
      if (museumCode) params.append('museum_code', museumCode);
      if (museumLevel) params.append('museum_level', museumLevel);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error getting installed tours for robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Install tours to a robot
   */
  async installToursToRobot(
    robotId: string,
    museumCode: string,
    museumLevel: string,
    tourUrls: string[],
  ): Promise<any> {
    try {
      const response = await fetch(`/api/tours/install/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          robot_id: robotId,
          museum_code: museumCode,
          museum_level: museumLevel,
          tour_urls: tourUrls,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error installing tours to robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Activate tours on a robot
   */
  async activateToursOnRobot(
    robotId: string,
    museumCode: string,
    museumLevel: string,
    tourUrls: string[],
  ): Promise<any> {
    try {
      const response = await fetch(`/api/tours/activate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          robot_id: robotId,
          museum_code: museumCode,
          museum_level: museumLevel,
          tour_urls: tourUrls,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error activating tours on robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Send/install a tour to a robot
   */
  async sendTourToRobot(robotId: string, tourId: number): Promise<any> {
    try {
      const response = await fetch(`/api/robots/${robotId}/tours/send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ tour_id: tourId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error sending tour ${tourId} to robot ${robotId}:`, error);
      throw error;
    }
  }

  /**
   * Map NHB status to our internal status
   */
  private mapStatus(nhbStatus: string): 'online' | 'offline' | 'busy' | 'charging' {
    const statusMap: { [key: string]: 'online' | 'offline' | 'busy' | 'charging' } = {
      initializing: 'offline',
      idle: 'online',
      busy: 'busy',
      executing: 'busy', // Map executing to busy
      charging: 'charging',
      error: 'offline',
      maintenance: 'offline',
    };

    return statusMap[nhbStatus.toLowerCase()] || 'offline';
  }
}

export const nhbApiService = new NHBApiService();

export type TourAssignmentRequest = {
  robot_id: string;
  museum_code: string;
  museum_level: string;
  tour_urls: string[];
};
